import API from '~config/api';
import { ApiPromise, DataResponse, ErrorResponse } from '~globals/types';

import {
  AdminReportSummaryFilter,
  AdminReportSummary,
  AdminReportStatisticFilter,
  AdminReportStatistic,
  AdminReportDownloadRoutes,
  AdminReportDownloadRouteItems,
  AdminReportDownloadItemTasks,
  AdminReportUserSatisfactionFilters,
  AdminReportUserSatisfaction,
  AdminReportHeatmapCoords,
  AdminReportItemsInProgressFilters,
  AdminReportItemsInProgressResponse,
  AdminReportItemsInProgressByRouteFilters,
  AdminReportItemsInProgressByRouteResponse,
  AdminReportItemsInProgressByZoneFilters,
  AdminReportItemsInProgressByZoneResponse,
  AdminReportItemsInProgressByCompanyFilters,
  AdminReportItemsInProgressByCompanyResponse,
} from './types';

export const ADMIN_REPORTS_ENPOINTS = {
  getReportSummary: '/api/v1/Admin/Report/ReportSummary',
  getReportStatistic: '/api/v1/Admin/Report/Statistic',
  getReportRoutes: '/api/v1/Admin/Report/Routes',
  getReportRouteItems: '/api/v1/Admin/Report/RouteItems',
  getReportItemTasks: '/api/v1/Admin/Report/ItemTasks',
  getReportUserSatisfaction: '/api/v1/Track/RouteItemSourvey/ReportSourvey',
  getReportFeedbackReviews: '/api/v1/Track/RouteItemSourvey/ReportSourveyCSV',
  getReportHeatmap: '/api/v1/Admin/Report/Heatmap',
  getItemsInProgress: '/api/v1/Admin/Report/ItemsInProgress',
  getItemsInProgressByRoute: '/api/v1/Admin/Report/ItemsInProgressByRoute',
  getItemsInProgressByZone: '/api/v1/Admin/Report/ItemsInProgressByZone',
  getItemsInProgressByCompany: '/api/v1/Admin/Report/ItemsInProgressByCompany',
};

export const getReportSummary = (
  body: AdminReportSummaryFilter,
): ApiPromise<DataResponse<AdminReportSummary>, ErrorResponse> =>
  API.post<DataResponse<AdminReportSummary>, ErrorResponse>(
    ADMIN_REPORTS_ENPOINTS.getReportSummary,
    body,
  );

export const getReportStatistic = (
  body: AdminReportStatisticFilter,
): ApiPromise<DataResponse<AdminReportStatistic>, ErrorResponse> =>
  API.post<DataResponse<AdminReportStatistic>, ErrorResponse>(
    ADMIN_REPORTS_ENPOINTS.getReportStatistic,
    body,
  );

export const getReportRoutes = (
  filters: AdminReportDownloadRoutes,
): ApiPromise<Blob, ErrorResponse> =>
  API.post<Blob, ErrorResponse>(
    ADMIN_REPORTS_ENPOINTS.getReportRoutes,
    filters,
    { responseType: 'blob', timeout: 120000 },
  );

export const getReportRouteItems = (
  filters: AdminReportDownloadRouteItems,
): ApiPromise<Blob, ErrorResponse> =>
  API.post<Blob, ErrorResponse>(
    ADMIN_REPORTS_ENPOINTS.getReportRouteItems,
    filters,
    { responseType: 'blob', timeout: 120000 },
  );

export const getReportItemTasks = (
  filters: AdminReportDownloadItemTasks,
): ApiPromise<Blob, ErrorResponse> =>
  API.post<Blob, ErrorResponse>(
    ADMIN_REPORTS_ENPOINTS.getReportItemTasks,
    filters,
    { responseType: 'blob', timeout: 120000 },
  );

export const getReportUserSatisfaction = (
  filters: AdminReportUserSatisfactionFilters,
): ApiPromise<DataResponse<AdminReportUserSatisfaction>, ErrorResponse> =>
  API.post<DataResponse<AdminReportUserSatisfaction>, ErrorResponse>(
    ADMIN_REPORTS_ENPOINTS.getReportUserSatisfaction,
    filters,
  );

export const getReportFeedbackReviews = (
  filters: AdminReportUserSatisfactionFilters,
): ApiPromise<Blob, ErrorResponse> =>
  API.post<Blob, ErrorResponse>(
    ADMIN_REPORTS_ENPOINTS.getReportFeedbackReviews,
    filters,
    { responseType: 'blob', timeout: 120000 },
  );

export const getReportHeatmap = (): ApiPromise<
  DataResponse<AdminReportHeatmapCoords[]>,
  ErrorResponse
> =>
  API.get<DataResponse<AdminReportHeatmapCoords[]>, ErrorResponse>(
    ADMIN_REPORTS_ENPOINTS.getReportHeatmap,
  );

export const getItemsInProgress = (
  filters: AdminReportItemsInProgressFilters,
): ApiPromise<
  DataResponse<AdminReportItemsInProgressResponse>,
  ErrorResponse
> =>
  API.post<DataResponse<AdminReportItemsInProgressResponse>, ErrorResponse>(
    ADMIN_REPORTS_ENPOINTS.getItemsInProgress,
    { ...filters, excelMode: false },
  );

export const getItemsInProgressToExport = (
  filters: AdminReportItemsInProgressFilters,
): ApiPromise<Blob, ErrorResponse> =>
  API.post<Blob, ErrorResponse>(
    ADMIN_REPORTS_ENPOINTS.getItemsInProgress,
    { ...filters, excelMode: true },
    { responseType: 'blob', timeout: 120000 },
  );

export const getItemsInProgressByRoute = (
  filters: AdminReportItemsInProgressByRouteFilters,
): ApiPromise<
  DataResponse<AdminReportItemsInProgressByRouteResponse>,
  ErrorResponse
> =>
  API.post<
    DataResponse<AdminReportItemsInProgressByRouteResponse>,
    ErrorResponse
  >(ADMIN_REPORTS_ENPOINTS.getItemsInProgressByRoute, filters);

export const getItemsInProgressByZone = (
  filters: AdminReportItemsInProgressByZoneFilters,
): ApiPromise<
  DataResponse<AdminReportItemsInProgressByZoneResponse>,
  ErrorResponse
> =>
  API.post<
    DataResponse<AdminReportItemsInProgressByZoneResponse>,
    ErrorResponse
  >(ADMIN_REPORTS_ENPOINTS.getItemsInProgressByZone, filters);

export const getItemsInProgressByCompany = (
  filters: AdminReportItemsInProgressByCompanyFilters,
): ApiPromise<
  DataResponse<AdminReportItemsInProgressByCompanyResponse>,
  ErrorResponse
> =>
  API.post<
    DataResponse<AdminReportItemsInProgressByCompanyResponse>,
    ErrorResponse
  >(ADMIN_REPORTS_ENPOINTS.getItemsInProgressByCompany, filters);
